import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useParams } from 'react-router-dom'
import { useCurrentHealthCenter, useCurrentMode } from '../../components/AppShell'
import { Patient, PatientAttributes, createPatient, fetchPatient, updatePatient } from '../../api/patients'
import { useMe } from '../../context/MeContext'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import {
  Alert,
  DateField,
  Panel,
  PanelBody,
  PanelHeading,
  PanelTitle,
  RadioGroup,
  SelectField,
  RutField,
  SubmitButton,
  TextField,
} from '../../components/common'
import { NO_RUT } from '../../lib/rut'
import { useImpression } from '../../lib/useImpression'
import { useNavigateTo } from '../../lib/spaPath'
import styles from './PatientFormPage.module.scss'

const EDUCATIONS = ['Ninguna', 'Basica', 'Media', 'Técnico', 'Superior']
const MARITAL_STATUS = ['Soltero', 'Casado', 'Separado', 'Viudo', 'Conviviente']
const PREVISIONS = ['Particular', 'Fonasa', 'Isapre']

const AUTOSAVE_EXCLUDED: (keyof PatientAttributes)[] = ['rut', 'health_center_id']

type FormValues = {
  rut: string
  name: string
  date_born: string
  education: string
  marital_status: string
  number_of_childrens: string
  prevision: string
  occupation: string
  sex: string
  phone_number: string
  email: string
  company_which_derives: string
  domicile: string
  commune: string
  representative_name: string
  representative_phone: string
  emergency_number: string
}

const EMPTY_FORM: FormValues = {
  rut: '',
  name: '',
  date_born: '',
  education: '',
  marital_status: '',
  number_of_childrens: '',
  prevision: '',
  occupation: '',
  sex: '',
  phone_number: '',
  email: '',
  company_which_derives: '',
  domicile: '',
  commune: '',
  representative_name: '',
  representative_phone: '',
  emergency_number: '',
}

function autosaveKey(): string {
  return `hc-autosave:${window.location.pathname}`
}

function getFormFromPatient(patient: Patient): FormValues {
  return {
    rut: patient.rut === NO_RUT ? '' : (patient.rut ?? ''),
    name: patient.name ?? '',
    date_born: patient.date_born ?? '',
    education: patient.education ?? '',
    marital_status: patient.marital_status ?? '',
    number_of_childrens: patient.number_of_childrens === null ? '' : String(patient.number_of_childrens),
    prevision: patient.prevision ?? '',
    occupation: patient.occupation ?? '',
    sex: patient.sex ?? '',
    phone_number: patient.phone_number ?? '',
    email: patient.email ?? '',
    company_which_derives: patient.company_which_derives ?? '',
    domicile: patient.domicile ?? '',
    commune: patient.commune ?? '',
    representative_name: patient.representative_name ?? '',
    representative_phone: patient.representative_phone ?? '',
    emergency_number: patient.emergency_number ?? '',
  }
}

function getFormWithDraft(form: FormValues): FormValues {
  const saved = window.localStorage.getItem(autosaveKey())
  if (!saved) {
    return form
  }
  try {
    const draft = JSON.parse(saved) as Partial<FormValues>
    const kept = Object.fromEntries(Object.entries(draft).filter(([field]) => !AUTOSAVE_EXCLUDED.includes(field as keyof PatientAttributes)))
    return { ...form, ...kept }
  } catch {
    window.localStorage.removeItem(autosaveKey())
    return form
  }
}

const iconLabel = (icon: string, label: string) => (
  <>
    <i className={`fa ${icon}`} /> {label}
  </>
)

const getOptionsIncluding = (value: string, options: readonly string[]) =>
  (value && !options.includes(value) ? [value, ...options] : options).map((option) => ({ value: option, label: option }))

export function loader({ params }: LoaderFunctionArgs): Promise<Patient> {
  return fetchPatient(Number(params.healthCenterId), params.patientId as string)
}

export default function PatientFormPage() {
  const me = useMe()
  const healthCenter = useCurrentHealthCenter()
  const mode = useCurrentMode()
  const params = useParams()
  const patientId = params.patientId ?? null
  const patient = useLoaderData() as Patient | undefined
  const [form, setForm] = useState<FormValues>(() => (patient ? getFormWithDraft(getFormFromPatient(patient)) : EMPTY_FORM))
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const customFormRef = useRef<HTMLDivElement>(null)
  const navigateTo = useNavigateTo()
  const totalPatients = useMemo(
    () => me.health_centers.reduce((sum, candidate) => sum + candidate.patients_count, 0),
    [me],
  )

  useEffect(() => {
    const toSave: Partial<FormValues> = { ...form }
    AUTOSAVE_EXCLUDED.forEach((field) => delete toSave[field as keyof FormValues])
    window.localStorage.setItem(autosaveKey(), JSON.stringify(toSave))
  }, [form])

  useEffect(() => {
    if (patient && customFormRef.current) {
      Object.entries(patient.custom_info).forEach(([name, value]) => {
        const input = customFormRef.current?.querySelector<HTMLInputElement>(`#patient_custom_info_${name}`)
        if (input) {
          input.value = value ?? ''
        }
      })
    }
  }, [patient])

  const showFirstPatientHint = totalPatients <= 1
  useImpression('Onboarding Hint Shown', showFirstPatientHint ? { hint: 'first_patient_details' } : null)

  if (!healthCenter) {
    return null
  }

  const setField = (field: keyof FormValues) => (value: string) => setForm((current) => ({ ...current, [field]: value }))

  const collectCustomInfo = (): Record<string, string> => {
    const result: Record<string, string> = {}
    customFormRef.current
      ?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input[id^="patient_custom_info_"], select[id^="patient_custom_info_"], textarea[id^="patient_custom_info_"]')
      .forEach((input) => {
        result[input.id.replace('patient_custom_info_', '')] = input.value
      })
    return result
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setErrors([])
    const rut = form.rut === '' && patient?.rut === NO_RUT ? NO_RUT : form.rut
    const attributes: PatientAttributes = { ...form, rut, health_center_id: healthCenter.id }
    try {
      const result = patientId
        ? await updatePatient(healthCenter.id, patientId, mode, attributes, collectCustomInfo())
        : await createPatient(healthCenter.id, mode, attributes, collectCustomInfo())
      if (patientId) {
        trackEvent('Patient Updated', undefined, { transport: 'sendBeacon' })
      } else {
        trackEvent('Patient Created', { source: 'form', has_rut: form.rut.trim().length > 0 }, { transport: 'sendBeacon' })
      }
      window.localStorage.removeItem(autosaveKey())
      navigateTo(result.record_path ?? `/health_centers/${healthCenter.id}/patients`, { notice: result.notice })
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.body) {
        const body = requestError.body as Record<string, string[]>
        trackEvent('Form Save Failed', { form: 'patient', reason: 'validation', status_code: requestError.status, fields: Object.keys(body) })
        setErrors(Object.entries(body).flatMap(([field, messages]) => messages.map((message) => `${field} ${message}`)))
      } else {
        trackEvent('Form Save Failed', {
          form: 'patient',
          reason: 'request',
          status_code: requestError instanceof ApiError ? requestError.status : undefined,
        })
        setErrors(['Ocurrió un error, inténtalo nuevamente'])
      }
      setSubmitting(false)
      window.scrollTo({ top: 0 })
    }
  }

  return (
    <div className="col-lg-8 col-lg-offset-2" style={{ marginTop: 10 }}>
      <Panel>
        <form className="patient" onSubmit={(event) => void handleSubmit(event)}>
          <PanelBody>
            <div className="col">
              {showFirstPatientHint && (
                <Alert kind="info" className={styles.firstAlert}>
                  ¡Muy bien, haz creado tu primer paciente!
                  <br />
                  Aquí puedes ingresar más información de tu paciente, cuando termines haz click en el boton "ACTUALIZAR PACIENTE"
                </Alert>
              )}
              {errors.length > 0 && (
                <Panel variant="danger" id="error_expl">
                  <PanelHeading>
                    <PanelTitle>
                      {errors.length} {errors.length === 1 ? 'error' : 'errores'} prohibited this patient from being saved:
                    </PanelTitle>
                  </PanelHeading>
                  <PanelBody>
                    <ul>
                      {errors.map((message, index) => (
                        <li key={index}>{message}</li>
                      ))}
                    </ul>
                  </PanelBody>
                </Panel>
              )}

              <div className="module-heading">
                <h3 className={`text-center ${styles.sectionHeading}`}>Información General</h3>
              </div>
              <div className="col-xs-12" style={{ marginBottom: 40 }}>
                <RutField
                  id="patient_rut"
                  className="col-md-3 col-xs-12"
                  labelStyle="static"
                  label={iconLabel('fa-id-card-o', 'RUT')}
                  value={form.rut}
                  readOnly={patient?.rut !== NO_RUT}
                  onChange={setField('rut')}
                />
                <TextField
                  id="patient_name"
                  className="col-md-6 col-xs-12"
                  label={iconLabel('fa-user', 'Nombre')}
                  value={form.name}
                  onChange={setField('name')}
                />
                <DateField
                  id="patient_date_born"
                  className="col-md-3 col-xs-6"
                  label={iconLabel('fa-birthday-cake', 'Fecha de Nacimiento')}
                  value={form.date_born}
                  onChange={setField('date_born')}
                />
                <SelectField
                  id="patient_education"
                  className="col-md-3 col-xs-6"
                  label={iconLabel('fa-university', 'Educación')}
                  value={form.education}
                  placeholder={form.education ? undefined : ''}
                  options={getOptionsIncluding(form.education, EDUCATIONS)}
                  onChange={setField('education')}
                />
                <SelectField
                  id="patient_marital_status"
                  className="col-md-3 col-xs-6"
                  label={iconLabel('fa-heart', 'Estado civil')}
                  value={form.marital_status}
                  placeholder={form.marital_status ? undefined : ''}
                  options={getOptionsIncluding(form.marital_status, MARITAL_STATUS)}
                  onChange={setField('marital_status')}
                />
                <TextField
                  id="patient_number_of_childrens"
                  className="col-md-3 col-xs-6"
                  label={iconLabel('fa-child', 'Número de hijos')}
                  value={form.number_of_childrens}
                  onChange={setField('number_of_childrens')}
                />
                <SelectField
                  id="patient_prevision"
                  className="col-md-3 col-xs-6"
                  label={iconLabel('fa-plus', 'Sistema de salud')}
                  value={form.prevision}
                  placeholder={form.prevision ? undefined : ''}
                  options={getOptionsIncluding(form.prevision, PREVISIONS)}
                  onChange={setField('prevision')}
                />
                <TextField
                  id="patient_occupation"
                  className="col-md-6 col-xs-6"
                  label={iconLabel('fa-suitcase', 'Ocupación')}
                  value={form.occupation}
                  onChange={setField('occupation')}
                />
                <RadioGroup
                  className="col-md-6 col-xs-6 control-group"
                  label={iconLabel('fa-venus-mars', 'Sexo')}
                  name="patient[sex]"
                  value={form.sex === '' ? null : form.sex}
                  onChange={setField('sex')}
                  options={[
                    { value: 'M', label: 'Masculino' },
                    { value: 'F', label: 'Femenino' },
                  ]}
                />
              </div>

              <div className="module-heading">
                <h3 className={`text-center ${styles.sectionHeading}`}>Información de Contacto</h3>
              </div>
              <div className="col-xs-12">
                <TextField
                  id="patient_phone_number"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-phone', 'Número de teléfono')}
                  value={form.phone_number}
                  onChange={setField('phone_number')}
                />
                <TextField
                  id="patient_email"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-envelope', 'Email')}
                  value={form.email}
                  onChange={setField('email')}
                />
                {patient && patient.derivations.length > 0 ? (
                  <SelectField
                    id="patient_company_which_derives"
                    className="col-md-4 col-xs-12"
                    label={iconLabel('fa-user', 'Ente que deriva')}
                    value={form.company_which_derives}
                    placeholder={form.company_which_derives ? undefined : ''}
                    options={getOptionsIncluding(form.company_which_derives, patient.derivations)}
                    onChange={setField('company_which_derives')}
                  />
                ) : (
                  <TextField
                    id="patient_company_which_derives"
                    className="col-md-4 col-xs-12"
                    label={iconLabel('fa-user', 'Ente que deriva')}
                    value={form.company_which_derives}
                    onChange={setField('company_which_derives')}
                  />
                )}
                <TextField
                  id="patient_domicile"
                  className="col-md-8 col-xs-12"
                  label={iconLabel('fa-map-marker', 'Domicilio')}
                  value={form.domicile}
                  onChange={setField('domicile')}
                />
                <TextField
                  id="patient_commune"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-map-marker', 'Comuna')}
                  value={form.commune}
                  onChange={setField('commune')}
                />
                <TextField
                  id="patient_representative_name"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-user', 'Nombre del representante o apoderado')}
                  value={form.representative_name}
                  onChange={setField('representative_name')}
                />
                <TextField
                  id="patient_representative_phone"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-phone', 'Teléfono del representante')}
                  value={form.representative_phone}
                  onChange={setField('representative_phone')}
                />
                <TextField
                  id="patient_emergency_number"
                  className="col-md-4 col-xs-6"
                  label={iconLabel('fa-ambulance', 'Número de emergencia')}
                  value={form.emergency_number}
                  onChange={setField('emergency_number')}
                />
              </div>
            </div>
            {patient && patient.custom_form_html && (
              <div ref={customFormRef} dangerouslySetInnerHTML={{ __html: patient.custom_form_html }} />
            )}
            <br />
            <br />
            <div className="text-right">
              <SubmitButton
                label={patientId ? 'Actualizar paciente' : 'Guardar paciente'}
                raised
                style={{ marginTop: 20 }}
                disabled={submitting}
              />
            </div>
          </PanelBody>
        </form>
      </Panel>
    </div>
  )
}
